import React from 'react';
import { useSelector } from 'react-redux';

import { selectDarkMode } from '../../../../../userBrowserSettings/store/browserSettings.selectors';
import { selectViewOnlyOutlineData } from '../../../../pages/contentOptimizer/viewOnly/store/viewOnlySettings.selectors';

import { useCurrentEditor } from '@tiptap/react';
import { useIconColor } from '../../../../../utils/hooks/useIconColor';
import { useOpen } from '../../../../../utils/hooks/useOpen';
import { useIsAIContentGeneration } from '../../../../../utils/hooks/useIsAIContentGeneration';
import useUpdateOutlineEditorContent from '../../hooks/outline/useUpdateOutlineEditorContent';

import { openNotification } from '../../../../../utils/helpers/openNotification';

import { notificationType } from '../../../../../constants/notificationType';
import { defaultBriefOutlineContent } from '../../constants/tiptapEditorConstants';

import { Popconfirm } from 'antd';
import MoreDropdown from '../../../moreDropdown/MoreDropdown';
import EditorToolbar from '../EditorToolbar';
import OutlineEditorMoreSettings from './OutlineEditorMoreSettings';
import OutlineEditorToolbarSections from './OutlineEditorToolbarSections';
import OutlineVersionHistory from './OutlineVersionHistory';

import CopySectionIcon from '../../../../../resources/icons/CopySectionIcon';
import RemoveIcon from '../../../../../resources/icons/outline/RemoveIcon';

const classes = {
    deletePopconfirm: 'delete-popconfirm',
    deletePopconfirmDark: 'delete-popconfirm-dark dark-mode',
};

const OutlineEditorToolbar = () => {
    const { editor } = useCurrentEditor();

    const darkMode = useSelector(selectDarkMode);
    const viewOnlyMode = useSelector(selectViewOnlyOutlineData);
    const { isAIContentLoading } = useIsAIContentGeneration();

    const iconColor = useIconColor();
    const { isOpen: isDropdownOpen, handleOpen: handleDropdownOpen } = useOpen();
    const handleUpdateEditor = useUpdateOutlineEditorContent();

    const handleCopyOutline = async () => {
        try {
            const htmlContent = editor.getHTML();
            const plainText = editor.getText();

            await navigator.clipboard.write([
                new ClipboardItem({
                    'text/html': new Blob([htmlContent], { type: 'text/html' }),
                    'text/plain': new Blob([plainText], { type: 'text/plain' }),
                }),
            ]);

            openNotification(
                notificationType.success,
                'Success',
                'Outline was successfully copied to clipboard'
            );
        } catch (err) {
            console.error('All clipboard methods failed:', err);
            openNotification(
                notificationType.error,
                'Error',
                'Failed to copy outline to clipboard'
            );
        } finally {
            handleDropdownOpen();
        }
    };

    const moreItems = [
        {
            key: 'copy-outline',
            label: (
                <div className='d-flex align-items-center' onClick={handleCopyOutline}>
                    <CopySectionIcon color={iconColor} size='16' />
                    <span>Copy Outline</span>
                </div>
            ),
        },
        {
            key: 'delete-outline',
            label: (
                <Popconfirm
                    placement='top'
                    okText='Yes'
                    cancelText='Cancel'
                    title='Delete this entire Brief Outline?'
                    overlayClassName={`${classes.deletePopconfirm} ${
                        darkMode ? classes.deletePopconfirmDark : ''
                    }`}
                    onConfirm={() => {
                        editor.commands.setContent(defaultBriefOutlineContent);
                        handleUpdateEditor(defaultBriefOutlineContent);
                        handleDropdownOpen();
                    }}
                    onCancel={() => {
                        handleDropdownOpen();
                    }}
                >
                    <div className='d-flex align-items-center'>
                        <RemoveIcon color={iconColor} size='18' />
                        <span>Delete Outline</span>
                    </div>
                </Popconfirm>
            ),
        },
    ];

    return (
        <EditorToolbar
            toolbarSections={<OutlineEditorToolbarSections />}
            versionHistorySection={<OutlineVersionHistory />}
            moreDropdown={
                <MoreDropdown
                    isViewOnly={viewOnlyMode}
                    disabled={isAIContentLoading}
                    moreItems={moreItems}
                    isDropdownOpen={isDropdownOpen}
                    handleDropdownOpen={handleDropdownOpen}
                    moreSettingsComponent={<OutlineEditorMoreSettings />}
                />
            }
        />
    );
};

export default OutlineEditorToolbar;
